'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { isPublicRoute } from '@/lib/utils/route-utils';
// Import our staging domain fix with dynamic import to avoid SSR issues
const StagingDomainAuthFix = dynamic(
  () => import('@/lib/hooks/StagingDomainAuthFix').then(mod => mod.StagingDomainAuthFix),
  { ssr: false }
);

// Import the unified AppHeader component
const AppHeader = dynamic(() =>
  import('@/components/ui/unified/AppHeader').then((mod) => mod.AppHeader),
  { ssr: false }
);


// Import the public search bar component
const PublicSearchBar = dynamic(() => 
  import('@/components/public/PublicSearchBar'),
  { ssr: false }
);

// Import the indicator component with no SSR to avoid hydration issues
const EnvironmentIndicator = dynamic(
  () => import('@/components/EnvironmentIndicator'),
  { ssr: false }
);

// Import the BellToastBridge for floating notifications
const BellToastBridge = dynamic(
  () => import('@/components/ui/bell-toast-bridge').then(mod => mod.BellToastBridge),
  { ssr: false }
);

// Import Momento components
import { MomentoProvider } from '@/components/momento/MomentoProvider';
const MomentoOverlay = dynamic(
  () => import('@/components/momento/MomentoOverlay').then(mod => mod.MomentoOverlay),
  { ssr: false }
);

// Import Admin Impersonation Banner
const ImpersonationBanner = dynamic(
  () => import('@/components/admin/ImpersonationBanner').then(mod => mod.ImpersonationBanner),
  { ssr: false }
);

/**
 * Client layout that uses server-side authentication
 * This is the standard layout after completing the auth system migration
 */
/**
 * Enhanced client layout component that safely uses auth
 * This layout is now aware that auth is initialized in the parent component
 * and can handle public routes differently from authenticated routes
 */
export default function ClientLayout({ 
  children 
}: { 
  children: React.ReactNode 
}) {
  const pathname = usePathname();
  const [isInConversation, setIsInConversation] = React.useState(false);
  const isRecordingRoute = pathname?.startsWith('/lk-recording');
  const isDashboardRoute = pathname?.startsWith('/dashboard');
  const isMomentoRoute = pathname?.startsWith('/momento');
  const isAdminRoute = pathname?.startsWith('/admin');
  const isRoomRoute = pathname?.startsWith('/rooms/');
  const isPublic = isPublicRoute(pathname);
  const isPublicLandingStyle = isPublic || pathname === '/' || pathname === '/start' ||
    pathname?.startsWith('/for-') || pathname?.startsWith('/for/') ||
    pathname === '/es' || pathname?.startsWith('/es/') ||
    pathname === '/fr' || pathname?.startsWith('/fr/') ||
    pathname?.startsWith('/home') ||
    pathname?.startsWith('/routines') ||
    pathname === '/pricing' || pathname === '/about' ||
    pathname === '/studio' || pathname === '/ai-transparency';
  const auth = useSupabaseAuth(); // Using the simplified auth context

  // Recording page runs in headless Chrome — skip all app chrome
  if (isRecordingRoute) {
    return <>{children}</>;
  }

  // Listen for LiveKit room connection state
  React.useEffect(() => {
    const checkRoomState = () => {
      // Check if we're in a LiveKit room by looking for the video conference element
      const isConnected = document.querySelector('.lk-video-conference') !== null;
      setIsInConversation(isConnected);
    };

    // Check initially and set up observer for DOM changes
    checkRoomState();

    const observer = new MutationObserver(checkRoomState);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [pathname]);

  return (
    <MomentoProvider>
      <div className="relative min-h-screen flex flex-col">
        {/* Add our staging domain fix only when authenticated or on protected routes */}
        {(!isPublic || auth.isAuthenticated) && <StagingDomainAuthFix />}

        {/* Use the unified AppHeader component with appropriate variant */}
        {/* Skip header for dashboard, momento, admin routes, and when in a conversation */}
        {!isDashboardRoute && !isMomentoRoute && !isAdminRoute && !(isRoomRoute && isInConversation) && (
          <AppHeader
            variant={isPublic ? 'public' : 'default'}
            showNav={true}
            showSearch={false}
            showMenu={!isPublic}
            landingMode={isPublicLandingStyle}
          />
        )}

        {/* Admin Impersonation Banner - Always render, it will check its own state */}
        <ImpersonationBanner />

        <main className="flex-1 w-full">
          {children}
        </main>

        <EnvironmentIndicator />
        <BellToastBridge />

        {/* Momento Overlay - Only show for authenticated users */}
        {auth.isAuthenticated && auth.profile?.id && (
          <MomentoOverlay userId={auth.profile.id} />
        )}
      </div>
    </MomentoProvider>
  );
}